import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppContext } from 'services/context';
import { useTranslation } from 'services/hooks';
import { ROUTES } from 'constants/index';
import { motion } from 'framer-motion';
import Container from 'components/Container';
import TextArea from 'components/Input/TextArea';
import Button from 'components/Button';
import Icon from 'components/Icon';
import classNames from 'classnames';
import './style.css';

const STAGE_TYPES = {
	WELCOME: 'welcome',
	RATE: 'rate',
	RATE_2: 'rate-2',
	THANKYOU: 'thankyou',
	THANKYOU_AGAIN: 'thankyou_again',
};

//used for animation
const wrapperVariants = {
	initial: { y: 10, opacity: 0 },
	animate: { y: 0, opacity: 1 },
	exit: { y: -10, opacity: 0 },
};

const TRANSLATION_KEY = 'review';

const ReviewPage = () => {
	const { Api, user } = useAppContext();
	const getTranslation = useTranslation();
	const { recordId } = useParams();
	const navigate = useNavigate();
	const [currentStage, setCurrentStage] = useState('');
	const [questions, setQuestions] = useState([]);
	const [userAnswers, setUserAnswers] = useState([]);
	const [invalidQuestions, setInvalidQuestions] = useState([]);
	const ContentClassName = classNames('ReviewPage__content', {
		welcome: currentStage === STAGE_TYPES.WELCOME,
		rate: currentStage === STAGE_TYPES.RATE,
		rate_2: currentStage === STAGE_TYPES.RATE_2,
		thankyou: currentStage === STAGE_TYPES.THANKYOU,
		thankyou_again: currentStage === STAGE_TYPES.THANKYOU_AGAIN,
	});

	const content = useMemo(() => {
		return [
			{
				id: '1',
				stage: 'welcome',
				title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_welcome_title`),
				text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_welcome_text`),
			},
			{
				id: '2',
				stage: 'rate',
				title: '',
				text: '',
			},
			{
				id: '3',
				stage: 'thankyou',
				title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_thankyou_title`),
				text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_thankyou_text`),
			},
			{
				id: '4',
				stage: 'thankyou_again',
				title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_thankyou_again_title`),
				text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_thankyou_again_text`),
			},
		];
	}, [getTranslation]);

	// ---> returns content by stage
	const contentByStage = useMemo(() => {
		let _content = content?.find(c => c.stage === currentStage);
		if (!_content) return;
		return _content;
	}, [content, currentStage]);

	// ---> courseName for title of welcome screen
	const courseName = useMemo(() => {
		const _courseName = questions?.courseName;
		if (!_courseName) return '';
		return _courseName;
	}, [questions]);

	// ---> all the user questions thats is Numeric - fill stars
	const StarsQuestions = useMemo(() => {
		return questions?.reviewQuestionsByGroupType?.filter(quest => quest.isAnswerNumeric);
	}, [questions]);

	// ---> all the user questions thats NOT Numeric - Open Question to fill
	const openQuestion = useMemo(() => {
		return questions?.reviewQuestionsByGroupType?.filter(quest => !quest.isAnswerNumeric);
	}, [questions]);

	// ---> gives the btn title by the stage
	const titleWithCourseName = useMemo(() => {
		switch (currentStage) {
			case STAGE_TYPES.WELCOME:
				return getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_welcome_title`)?.replace('[course]', courseName || '');
			case STAGE_TYPES.THANKYOU:
			case STAGE_TYPES.THANKYOU_AGAIN:
				return getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_thankyou_title`);
		}
		return '';
	}, [currentStage, getTranslation]);

	const _handlePressbackButton = () => {
		navigate(ROUTES.PERSONAL_AREA);
	};
	// ---> fetch all questions data by recordId
	useEffect(() => {
		(async () => {
			let questions = await Api.GetReviewQuestions(recordId);
			if (questions?.isQuestionnaireExists) {
				setCurrentStage(STAGE_TYPES.THANKYOU_AGAIN);
			} else {
				setCurrentStage(STAGE_TYPES.WELCOME);
			}
			if (questions) setQuestions(questions);
		})();
	}, [recordId, user?.token]);

	// ---> gives the btn title by the stage
	const btnTitle = useMemo(() => {
		switch (currentStage) {
			case STAGE_TYPES.WELCOME:
				return getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_welcome_button`);
			case STAGE_TYPES.RATE:
			case STAGE_TYPES.RATE_2:
				return getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_rate_button`);
			case STAGE_TYPES.THANKYOU:
				return '';
		}
		return '';
	}, [currentStage, getTranslation]);

	// ---> handle saving of user answers
	const _handleAnswerChange = ({ rate, questionId, textAnswer }) => {
		const newAnswer = {
			questionId: questionId,
			answerValue: rate || textAnswer,
		};
		const prevAnswers = userAnswers.filter(a => a.questionId !== questionId);
		prevAnswers.push(newAnswer);
		setUserAnswers(prevAnswers);

		//--if user answer on question that he didnt answered before and the error validation is occured.
		if (newAnswer.answerValue) {
			setInvalidQuestions(prevInvalidQuestions => {
				return prevInvalidQuestions.filter(question => question.questionId !== questionId);
			});
		}
	};

	// ---> validates user answer on submit button
	const validateAnswer = (questionList, userAnswers) => {
		const _invalidQuestions = [];
		let isValid = true;
		questionList.forEach(question => {
			if (!question.isMandatory) return;
			const _currentAnswer = userAnswers.find(answer => answer.questionId === question.questionId);
			if (!_currentAnswer?.answerValue) {
				_invalidQuestions.push({ ...question });
				isValid = false;
			}
		});

		return { isValid, _invalidQuestions };
	};

	// ---> save the answers that client answers
	const saveReviewQuestions = async () => {
		try {
			if (currentStage === STAGE_TYPES.RATE || currentStage === STAGE_TYPES.RATE_2) {
				const questionByStage = currentStage === STAGE_TYPES.RATE ? StarsQuestions : openQuestion;
				const { isValid, _invalidQuestions } = validateAnswer(questionByStage, userAnswers);
				setInvalidQuestions(_invalidQuestions);
				if (!isValid) {
					return;
				}
				handleStageChange();
			}

			const _courseReviewAnswers = {
				courseReviewAnswers: userAnswers?.map(answer => ({
					questionId: answer.questionId,
					answerValue: answer.answerValue,
				})),
				recordId: recordId,
			};

			if (currentStage === STAGE_TYPES.RATE_2 && _courseReviewAnswers.courseReviewAnswers.length !== 0) {
				const res = await Api.InsertReviewQuestionnaireAnswers(_courseReviewAnswers);
				if (res) {
					setCurrentStage(STAGE_TYPES.THANKYOU);
				}
			}
			handleStageChange();
		} catch (error) {
			console.warn(error);
		}
	};

	// ---> handles the stages change - set the stage by currentStage state
	const handleStageChange = () => {
		switch (currentStage) {
			case STAGE_TYPES.WELCOME:
				setCurrentStage(STAGE_TYPES.RATE);
				return;
			case STAGE_TYPES.RATE:
				setCurrentStage(STAGE_TYPES.RATE_2);
				return;
			case STAGE_TYPES.RATE_2:
				return;
			default:
				break;
		}
	};

	// ---> renders specific components by stage - [welcome , rate , rate-2 , thankyou,thankyou-again]
	const renderByStage = () => {
		switch (currentStage) {
			case STAGE_TYPES.WELCOME:
			case STAGE_TYPES.THANKYOU:
			case STAGE_TYPES.THANKYOU_AGAIN:
				return (
					<Content
						key={contentByStage?.id}
						title={titleWithCourseName}
						courseName={courseName}
						text={contentByStage?.text}
						withButton
						showSign={currentStage === STAGE_TYPES.THANKYOU || currentStage === STAGE_TYPES.THANKYOU_AGAIN}
						onClick={saveReviewQuestions}
					/>
				);
			case STAGE_TYPES.RATE:
				return StarsQuestions?.map((quest, idx) => (
					<ReviewQuestion
						key={quest.questionId}
						id={idx + 1}
						questionId={quest.questionId}
						title={quest.questionText}
						onChange={_handleAnswerChange}
						showValidationError={invalidQuestions.some(invalidQuestion => invalidQuestion.questionId === quest.questionId)}
					/>
				));
			case STAGE_TYPES.RATE_2:
				return openQuestion?.map(quest => (
					<ReviewQuestion
						key={quest.questionId}
						questionId={quest.questionId}
						title={quest.questionText}
						openQuestion
						onAnswer={_handleAnswerChange}
						showValidationError={invalidQuestions.some(invalidQuestion => invalidQuestion.questionId === quest.questionId)}
					/>
				));
			default:
				return null;
		}
	};

	return (
		<div className="ReviewPage">
			<Container>
				{(currentStage === STAGE_TYPES.THANKYOU || currentStage === STAGE_TYPES.THANKYOU_AGAIN) && (
					<Button
						small
						type="outline"
						startIcon="houseIcon"
						extraClassName="goBack"
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_back_button`)}
						onClick={() => _handlePressbackButton()}
					/>
				)}
				<motion.div key={currentStage} {...wrapperVariants}>
					<div className="ReviewPage__wrapper">
						<div className={ContentClassName}>
							{renderByStage()}
							{currentStage !== STAGE_TYPES.THANKYOU && currentStage !== STAGE_TYPES.THANKYOU_AGAIN && (
								<Button title={btnTitle} endIcon="rightArrow" onClick={saveReviewQuestions} />
							)}
						</div>
					</div>
				</motion.div>
			</Container>
		</div>
	);
};

export default ReviewPage;

const Content = ({ text, showSign, title, sign = 'Galway Academy' }) => {
	return (
		<div className="Content">
			<div className="Content__title">{title}</div>
			<div className="Content__text" dangerouslySetInnerHTML={{ __html: text }}></div>
			{showSign && sign && <div className="Content__sign"></div>}
		</div>
	);
};

const ReviewQuestion = ({ id, questionId, title, openQuestion, onChange, onAnswer, showValidationError }) => {
	const [showError, setShowError] = useState(false);
	const getTranslation = useTranslation();

	useEffect(() => {
		if (showValidationError) {
			setShowError(true);
		} else {
			setShowError(false);
		}
	}, [showError, showValidationError]);

	const errorTextByLang = useMemo(() => {
		return getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_question_error_text`);
	}, [getTranslation]);

	const updateStates = textAnswer => {
		onAnswer({ questionId, textAnswer });
	};

	const titleByOpenQuestion = () => {
		if (title && openQuestion) {
			return <div className="ReviewQuestion__title" dangerouslySetInnerHTML={{ __html: title }} />;
		}
		return (
			<div className="ReviewQuestion__title">
				{id && `${id}. `}
				{title}
			</div>
		);
	};

	const questionType = () => {
		if (!openQuestion)
			return <Rate questionId={questionId} onChange={onChange} showValidationError={showError} errorText={errorTextByLang} />;
		return (
			<>
				<TextArea maxlength={500} onChange={e => updateStates(e?.target?.value)} />
				{showError && <div className="validation_error">{errorTextByLang}</div>}
			</>
		);
	};

	return (
		<div className="ReviewQuestion" key={questionId}>
			{titleByOpenQuestion()}
			{questionType()}
		</div>
	);
};

export const Rate = ({
	stars = 5,
	starSize = 30,
	onChange,
	starColor,
	questionId,
	showValidationError,
	errorText,
	readOnly = false,
	value = 0,
}) => {
	const [rating, setRating] = useState(0);
	const [hover, setHover] = useState(0);
	const getTranslation = useTranslation();

	// keep internal state in sync if parent changes "value"
	useEffect(() => {
		setRating(value);
	}, [value]);

	const updateStates = rate => {
		if (readOnly) return;
		setRating(rate);
		onChange({ questionId, rate });
	};

	const handleMouseEnter = index => {
		if (readOnly) return;
		setHover(index);
	};

	const handleMouseLeave = () => {
		if (readOnly) return;
		setHover(rating);
	};

	const currentValue = readOnly ? value : hover || rating;

	return (
		<div className="Rate">
			<div className="Rate__stars">
				{[...Array(stars)].map((star, index) => {
					const starIndex = index + 1;
					const isOn = starIndex <= currentValue;
					return (
						<div
							key={index}
							className={`Rate__star ${isOn ? 'on' : 'off'} ${readOnly ? 'Rate__star--readonly' : ''}`}
							onClick={() => updateStates(starIndex)}
							onMouseEnter={() => handleMouseEnter(starIndex)}
							onMouseLeave={handleMouseLeave}
							disabled={readOnly}
						>
							<Icon icon="starIcon" size={starSize} color={starColor} />
						</div>
					);
				})}
				{showValidationError && (
					<div className="validation_error">{errorText || getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_question_error_text`)}</div>
				)}
			</div>
		</div>
	);
};
